import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { FiscalYearGetter } from '@/store/fiscalYear/fiscalYear.module';
import { FiscalYear } from '@/store/fiscalYear/fiscalYear.model';
import { CabinetGetter } from '@/store/cabinet/cabinet.module';
import { Cabinet } from '@/store/cabinet/cabinet.model';
import { CabinetMinisterGetter } from '@/store/cabinetMinister/cabinetMinister.module';
import { CabinetMinister } from '@/store/cabinetMinister/cabinetMinister.model';
import cabinetMinisterService from '@/store/cabinetMinister/cabinetMinister.service';
import { Region } from '@/store/region/region.model';
import { AppointmentRegionGetter } from '@/store/appointmentRegion/appointmentRegion.module';
import { AppointmentRegion } from '@/store/appointmentRegion/appointmentRegion.model';
import { Province } from '@/store/province/province.model';
import { ProjectResultAction, ProjectResultGetter } from '@/store/projectResult/projectResult.module';
import { ProjectSummary, ProjectChart } from '@/store/projectResult/projectResult.model';
import ResultProjectTypeComponent from '@/components/resultProjectType/ResultProjectType.vue';
import LineChartComponent from '@/components/charts/lineChart/LineChart.vue';
import BarChartComponent from '@/components/charts/barChart/BarChart.vue';
import DoughnutChartComponent from '@/components/charts/doughnutChart/DoughnutChart.vue';
import LoadingComponent from '@/components/loading/Loading.vue';

export default defineComponent({
  name: 'DashboardComponent',
  components: {
    ResultProjectTypeComponent,
    LineChartComponent,
    BarChartComponent,
    DoughnutChartComponent,
    LoadingComponent,
  },
  props: {
    fiscalYearId: {
      type: Number,
    },
    cabinetId: {
      type: Number,
    },
    cabinetMinisterId: {
      type: Number,
    },
    ministerId: {
      type: Number,
    },
    appointmentRegionId: {
      type: Number,
    },
    regionId: {
      type: Number,
    },
    provinceId: {
      type: Number,
    }
  },
  setup: (props) => {
    const store = useStore();
    const fiscalYear = computed<FiscalYear | undefined>(() => store.getters[FiscalYearGetter.BY_ID](props.fiscalYearId));
    const cabinet = computed<Cabinet | undefined>(() => store.getters[CabinetGetter.BY_ID](props.cabinetId));
    const cabinetsByFiscalYear = computed<Cabinet[]>(() => store.getters[CabinetGetter.BY_FISCAL_YEAR]);
    const cabinetMinister = computed<CabinetMinister | undefined>(() => store.getters[CabinetMinisterGetter.BY_ID](props.cabinetMinisterId));
    const region = computed<Region | undefined>(() => cabinetMinister.value?.regions.find(m => m.id === props.regionId));
    const appointmentRegion = computed<AppointmentRegion | undefined>(() => store.getters[AppointmentRegionGetter.BY_ID](props.appointmentRegionId));
    const province = computed<Province | undefined>(() => appointmentRegion.value?.provinces.find(m => m.id === props.provinceId));

    // The minister summary/chart aggregate every cabinet order overlapping the
    // fiscal year for this minister (not just the one picked above), so the
    // subtitle needs the full list of orders instead of just `cabinet.value`.
    const fiscalYearCabinetMinisters = ref<CabinetMinister[]>([]);
    const fetchFiscalYearCabinetMinisters = (fiscalYearId?: number) => {
      if (fiscalYearId) {
        cabinetMinisterService.FetchByFiscalYear(fiscalYearId)
          .then((response) => { fiscalYearCabinetMinisters.value = response.data; });
      }
    };
    const relatedCabinetNames = computed<string[]>(() => {
      if (!cabinetMinister.value) {
        return [];
      }

      const ministerId = cabinetMinister.value.minister.id;
      const cabinetIds = new Set(
        fiscalYearCabinetMinisters.value
          .filter(m => m.minister.id === ministerId)
          .map(m => m.cabinetId)
      );

      return cabinetsByFiscalYear.value
        .filter(c => cabinetIds.has(c.id))
        .sort((a, b) => a.startDate.getTime() - b.startDate.getTime())
        .map(c => c.name);
    });

    const description = computed(() => {
      const provinceName = (province.value) ? ` > ${province.value.name !== 'กรุงเทพมหานคร' ? 'จังหวัด' : ''}${province.value.name}` : '';

      // Once drilled into a region (by clicking a minister's region badge or
      // via the region filter), the summary aggregates by province membership
      // only - not by which minister/cabinet order approved the project - so
      // don't imply the numbers are scoped to whichever minister was selected.
      if (region.value) {
        return `${region.value.name}${provinceName}`;
      }

      const cabinetMinisterName = (cabinetMinister.value) ? ` > ${cabinetMinister.value?.minister.fullName}` : '';

      // Top-level (no drill-down): the summary below aggregates every project
      // whose own fiscal year matches this page, regardless of which cabinet
      // order it falls under, so don't imply the numbers are scoped to
      // specific orders.
      if (!cabinetMinister.value && cabinetsByFiscalYear.value.length > 0) {
        return 'สรุปผลรวมโครงการทั้งหมดในปีงบประมาณนี้';
      }

      const cabinetName = (relatedCabinetNames.value.length > 1)
        ? `คำสั่งที่เกี่ยวข้อง: ${relatedCabinetNames.value.join(', ')}`
        : cabinet.value?.name;
      return `${cabinetName}${cabinetMinisterName}`;
    });
    const mode = computed(() => (!props.provinceId) ? (!props.regionId) ? (!props.ministerId) ? 'OFFICER' : 'MINISTER' : 'REGION' : 'PROVINCE');
    const target = computed(() => (!props.provinceId) ? (!props.regionId) ? (!props.ministerId) ? null : cabinetMinister.value?.minister : region.value : province.value);
    const summary = computed<ProjectSummary>(() => store.getters[ProjectResultGetter.SUMMARY]);
    const chart = computed<ProjectChart>(() => store.getters[ProjectResultGetter.CHART]);
    const isLoading = computed(() => store.getters[ProjectResultGetter.STATUS] === 'init' || store.getters[ProjectResultGetter.STATUS] === 'loading');

    onMounted(() => {
      fetchFiscalYearCabinetMinisters(props.fiscalYearId);

      if (props.provinceId) {
        provinceSummary(props.fiscalYearId, props.provinceId);
      } else if (props.regionId) {
        regionSummary(props.fiscalYearId, props.regionId);
      } else if (props.ministerId) {
        ministerSummary(props.fiscalYearId, props.ministerId);
      } else {
        getSummary(props.fiscalYearId);
      }
    });

    watch(() => props.fiscalYearId, (fiscalYearId) => {
      fetchFiscalYearCabinetMinisters(fiscalYearId);
    });

    watch(() => [props.fiscalYearId, props.ministerId, props.regionId, props.provinceId], ([fiscalYearId, ministerId, regionId, provinceId]) => {
      if (provinceId) {
        provinceSummary(props.fiscalYearId, provinceId);
      } else if (regionId) {
        regionSummary(props.fiscalYearId, regionId);
      } else if (ministerId) {
        ministerSummary(props.fiscalYearId, ministerId);
      } else {
        getSummary(fiscalYearId);
      }
    });

    const provinceSummary = (fiscalYearId?: number, provinceId?: number) => {
      store.dispatch(ProjectResultAction.GET_PROVINCE_CHART, {
        fiscalYearId: fiscalYearId,
        provinceId: provinceId,
      }).then(() => store.dispatch(ProjectResultAction.GET_PROVINCE_SUMMARY, {
          fiscalYearId: fiscalYearId,
          provinceId: provinceId,
        }));
    };

    const regionSummary = (fiscalYearId?: number, regionId?: number) => {
      store.dispatch(ProjectResultAction.GET_REGION_CHART, {
        fiscalYearId: fiscalYearId,
        regionId: regionId,
      }).then(() => store.dispatch(ProjectResultAction.GET_REGION_SUMMARY, {
          fiscalYearId: fiscalYearId,
          regionId: regionId,
        }));
    };

    const ministerSummary = (fiscalYearId?: number, ministerId?: number) => {
      store.dispatch(ProjectResultAction.GET_MINISTER_CHART, {
        fiscalYearId: fiscalYearId,
        ministerId: ministerId,
      }).then(() => store.dispatch(ProjectResultAction.GET_MINISTER_SUMMARY, {
          fiscalYearId: fiscalYearId,
          ministerId: ministerId,
        }));
    };

    const getSummary = (fiscalYearId?: number) => {
      if (fiscalYearId) {
        store.dispatch(ProjectResultAction.GET_CHART, {
          fiscalYearId: fiscalYearId,
        }).then(() =>
          store.dispatch(ProjectResultAction.GET_SUMMARY, {
            fiscalYearId: fiscalYearId,
          }));
      }
    };

    return {
      fiscalYear,
      description,
      summary,
      mode,
      target,
      chart,
      isLoading,
    };
  },
});
